import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaHeadphones } from 'react-icons/fa';
import { getBooks, getSubscriptionPlans } from '../helpers/backend_helper';
import {
    canAccessAudiobooks,
    findSubscriptionPlanForMonths,
    getSubscriptionCheckoutPath,
    getSubscriptionPlansPayload,
    normalizeBook,
    resolveUploadAsset,
} from '../helpers/utils';
import BookActionButtons from './library/BookActionButtons';
import { helperFunction } from '../helpers/api_call_helper';
import { useAuth } from '../context/AuthContext';

function WaveAnimation({ active, color }) {
    return (
        <div className={`audio-wave ${active ? 'audio-wave-playing' : ''}`} aria-hidden="true">
            {[...Array(5)].map((_, i) => (
                <span key={i} className="audio-wave-bar" style={{ '--bar-color': color, '--delay': `${i * 0.12}s` }} />
            ))}
        </div>
    );
}

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
};

export default function Audiobooks({ preview = false }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [activeAudioId, setActiveAudioId] = useState(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [audioProgress, setAudioProgress] = useState({ current: 0, duration: 0 });
    const [apiAudiobooks, setApiAudiobooks] = useState([]);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasLoadedAudiobooks, setHasLoadedAudiobooks] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        setHasLoadedAudiobooks(false);

        helperFunction(
            async () => {
                const [booksResponse, plansResponse] = await Promise.all([
                    getBooks({ type: 'audio', limit: preview ? 3 : 100 }),
                    getSubscriptionPlans({ limit: 12 }),
                ]);
                const books = (booksResponse?.data?.data || [])
                    .map(normalizeBook)
                    .slice(0, preview ? 3 : undefined);
                const plans = getSubscriptionPlansPayload(plansResponse);
                if (mounted) setApiAudiobooks(books);
                if (mounted) setSubscriptionPlans(plans);
                if (mounted) setHasLoadedAudiobooks(true);
                return booksResponse;
            },
            setLoading,
            undefined,
            () => {
                if (mounted) setHasLoadedAudiobooks(true);
            },
        );

        return () => {
            mounted = false;
        };
    }, [preview]);

    const getAudiobookPlan = () => (
        findSubscriptionPlanForMonths(
            subscriptionPlans,
            6,
            { id: '', name: 'Half-Yearly', price: 2399, period: '/6 months' },
        )
    );

    const goToSixMonthSubscription = () => {
        navigate(getSubscriptionCheckoutPath(getAudiobookPlan()));
    };

    const getPlayerState = () => {
        const hasFullAccess = canAccessAudiobooks(user);
        const seekMax = hasFullAccess
            ? (audioProgress.duration || 0)
            : Math.min(audioProgress.duration || 30, 30);

        return {
            hasFullAccess,
            seekMax,
            seekValue: Math.min(audioProgress.current, seekMax || audioProgress.current),
            durationLabel: hasFullAccess ? formatTime(audioProgress.duration) : formatTime(seekMax || 30),
        };
    };

    const togglePlay = (item) => {
        const audio = audioRef.current;
        if (!audio) return;

        if (activeAudioId === item.id) {
            if (isAudioPlaying) {
                audio.pause();
                setIsAudioPlaying(false);
                return;
            }

            audio.play()
                .then(() => setIsAudioPlaying(true))
                .catch((error) => {
                    console.error('Playback error:', error);
                    setIsAudioPlaying(false);
                });
            return;
        }

        const audioSrc = resolveUploadAsset(item.mediaUrl || item.fileUrl || item.pdf);
        if (!audioSrc) return;

        setActiveAudioId(item.id);
        setAudioProgress({ current: 0, duration: 0 });
        audio.src = audioSrc;
        audio.currentTime = 0;
        audio.play()
            .then(() => setIsAudioPlaying(true))
            .catch((error) => {
                console.error('Playback error:', error);
                setIsAudioPlaying(false);
            });
    };

    const handleSeek = (event) => {
        const audio = audioRef.current;
        if (!audio) return;
        const nextTime = Number(event.target.value);
        audio.currentTime = nextTime;
        setAudioProgress((current) => ({ ...current, current: nextTime }));
    };

    const handleLoadedMetadata = () => {
        const audio = audioRef.current;
        if (!audio) return;
        setAudioProgress({
            current: audio.currentTime || 0,
            duration: audio.duration || 0,
        });
    };

    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (!audio) return;

        setAudioProgress({
            current: audio.currentTime || 0,
            duration: audio.duration || 0,
        });

        if (!canAccessAudiobooks(user) && audio.currentTime >= 30) {
            audio.pause();
            setIsAudioPlaying(false);
            goToSixMonthSubscription();
        }
    };

    const handleAudioEnded = () => {
        setIsAudioPlaying(false);
        setAudioProgress((current) => ({ ...current, current: 0 }));
        if (audioRef.current) audioRef.current.currentTime = 0;
    };

    const handleAudioError = () => {
        console.error('Audio failed to load');
        setIsAudioPlaying(false);
    };

    const closePlayer = () => {
        audioRef.current?.pause();
        setIsAudioPlaying(false);
        setActiveAudioId(null);
        setAudioProgress({ current: 0, duration: 0 });
    };

    const activeBook = apiAudiobooks.find((book) => book.id === activeAudioId);
    const playerState = getPlayerState();

    const renderAudiobookSkeletonCards = () => (
        Array.from({ length: 3 }).map((_, index) => (
            <div className="book-card reveal in-view detail-skeleton" key={`audio-skeleton-${index}`}>
                <div className="book-cover detail-skeleton-block" />
                <div className="book-info detail-skeleton-stack">
                    <div className="detail-skeleton-line detail-skeleton-label" />
                    <div className="detail-skeleton-line detail-skeleton-heading" />
                    <div className="detail-skeleton-line detail-skeleton-text-sm" />
                    <div className="detail-skeleton-row">
                        <div className="detail-skeleton-circle" />
                        <div className="detail-skeleton-line detail-skeleton-wide" />
                    </div>
                    <div className="detail-skeleton-line detail-skeleton-button" />
                </div>
            </div>
        ))
    );

    const renderAudiobookCard = (book, idx) => {
        const isActive = activeAudioId === book.id;
        const isPlaying = isActive && isAudioPlaying;
        const hasFullAccess = canAccessAudiobooks(user);
        const waveColor = book.waveColor || '#C9A962';
        const detailPath = `/book/${book.slug || book.id}`;

        return (
            <div
                key={book.id}
                className={`book-card reveal in-view ${isActive ? 'audiobook-playing' : ''}`}
                style={{ transitionDelay: `${idx * 0.1}s` }}
                onClick={() => navigate(detailPath)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => event.key === 'Enter' && navigate(detailPath)}
                aria-label={`View details for ${book.title}`}
            >
                <div className="book-cover" style={{ '--cover-accent': waveColor }}>
                    {book.cover && (book.cover.startsWith('data:') || book.cover.startsWith('http') || book.cover.length > 5) ? (
                        <img
                            src={book.cover}
                            alt={book.imageAlt || book.title}
                            loading="lazy"
                            decoding="async"
                            width="180"
                            height="240"
                        />
                    ) : (
                        <span style={{ fontSize: '5rem', lineHeight: 1, opacity: 0.5 }}><FaHeadphones /></span>
                    )}

                    <div className="book-cover-overlay">
                        <div className="absolute top-3 right-3" onClick={(event) => event.stopPropagation()}>
                            <BookActionButtons book={book} variant="card" />
                        </div>
                    </div>
                </div>

                <div className="book-info">
                    <div className="book-category audiobook-card-meta">
                        <span title={book.genre || book.category || 'Audiobook'}>
                            {book.genre || book.category || 'Audiobook'}
                        </span>
                        <span className="audiobook-length-pill">
                            {book.duration || 'Full length'}
                        </span>
                    </div>
                    <h3 className="book-title" style={{ marginBottom: '4px' }}>{book.title}</h3>
                    <p className="book-author" style={{ marginBottom: '8px' }}>Narrated by {book.narrator || book.author}</p>

                    <div className="audiobook-controls" style={{ marginBottom: '15px' }}>
                        <button
                            className={`btn-play ${isPlaying ? 'btn-play-active' : ''}`}
                            type="button"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                togglePlay(book);
                            }}
                            aria-label={isPlaying ? `Pause ${book.title}` : `Play ${book.title}`}
                        >
                            {isPlaying ? (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="6" y="4" width="4" height="16" />
                                    <rect x="14" y="4" width="4" height="16" />
                                </svg>
                            ) : (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    <polygon points="5 3 19 12 5 21 5 3" />
                                </svg>
                            )}
                        </button>
                        <WaveAnimation active={isPlaying} color={waveColor} />
                        <span className="audiobook-duration">
                            {isPlaying ? 'Playing now' : isActive ? 'Paused' : 'Sample available'}
                        </span>
                    </div>

                    <div className="book-price" style={{ borderTop: '1px solid var(--border)', paddingTop: '15px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span className="price" style={{ fontSize: '1rem' }}>
                                {hasFullAccess ? 'Full Access' : '6-Month Plan'}
                            </span>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>
                                {hasFullAccess ? 'You have full access to this audiobook' : 'Included with 6-month and above'}
                            </span>
                        </div>
                        <button
                            className="view-details"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (hasFullAccess) {
                                    togglePlay(book);
                                } else {
                                    goToSixMonthSubscription();
                                }
                            }}
                            style={{ backgroundColor: 'var(--gold)', color: '#000', border: 'none', position: 'relative', zIndex: 10 }}
                        >
                            {hasFullAccess ? (isPlaying ? 'Pause Audio' : isActive ? 'Resume Audio' : 'Listen Now') : 'Get Access'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="section audiobooks-section" id="audiobooks">
            <audio
                ref={audioRef}
                onEnded={handleAudioEnded}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onError={handleAudioError}
            />

            <div className="section-header-with-nav reveal in-view">
                <div className="shwn-text">
                    <div className="section-label">Listen Anywhere</div>
                    <h2 className="section-title">Audiobook Collection</h2>
                    <p className="section-subtitle">
                        Professional narrations of your favorite books. Perfect for multitasking.
                    </p>
                </div>
                {preview && (
                    <button className="preview-more-btn" type="button" onClick={() => navigate('/audiobooks')}>
                        More Audio Books <FaArrowRight />
                    </button>
                )}
            </div>

            <div className="content-preview-grid books-preview-grid">
                {(!hasLoadedAudiobooks || loading) && !apiAudiobooks.length && renderAudiobookSkeletonCards()}
                {hasLoadedAudiobooks && !loading && !apiAudiobooks.length && (
                    <div className="book-card reveal in-view book-info" style={{ color: 'var(--text-dim)' }}>
                        No audiobooks available yet.
                    </div>
                )}
                {apiAudiobooks.map((book, idx) => renderAudiobookCard(book, idx))}
            </div>

            {activeBook && (
                <div className="audiobook-modal-shell" role="dialog" aria-label="Audiobook player">
                    <div className="audiobook-modal">
                        <div className="audiobook-modal-cover">
                            {activeBook.cover ? (
                                <img src={activeBook.cover} alt={activeBook.title} loading="lazy" decoding="async" />
                            ) : (
                                <FaHeadphones />
                            )}
                        </div>
                        <div className="audiobook-modal-main">
                            <div className="audiobook-modal-copy">
                                <span>{isAudioPlaying ? 'Now playing' : 'Ready to play'}</span>
                                <h3>{activeBook.title}</h3>
                                <p>Narrated by {activeBook.narrator || activeBook.author}</p>
                            </div>
                            <div className="audiobook-modal-controls">
                                <button
                                    type="button"
                                    className="audiobook-modal-play"
                                    onClick={() => togglePlay(activeBook)}
                                    aria-label={isAudioPlaying ? 'Pause audio' : 'Play audio'}
                                >
                                    {isAudioPlaying ? (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <rect x="6" y="4" width="4" height="16" />
                                            <rect x="14" y="4" width="4" height="16" />
                                        </svg>
                                    ) : (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <polygon points="5 3 19 12 5 21 5 3" />
                                        </svg>
                                    )}
                                </button>
                                <div className="audiobook-modal-progress">
                                    <div className="audiobook-modal-time">
                                        <span>{formatTime(audioProgress.current)}</span>
                                        <span>{playerState.durationLabel}</span>
                                    </div>
                                    <input
                                        className="audiobook-seek"
                                        type="range"
                                        min="0"
                                        max={playerState.seekMax || 1}
                                        step="0.1"
                                        value={playerState.seekValue}
                                        onChange={handleSeek}
                                        aria-label={`Seek ${activeBook.title}`}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="audiobook-modal-close"
                                    onClick={closePlayer}
                                    aria-label="Close audio player"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
